import React from 'react';
import { Skeleton } from '../../../components/ui/skeleton';
import { Card, CardContent, CardHeader } from '../../../components/ui/card';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '../../../components/ui/breadcrumb';

const OrderDetailSkeleton = () => {
  return (
    <div className="bg-white min-h-screen">
      <div className="flex justify-center w-full max-w-screen-xl mx-auto">
        <div className="space-y-6 w-full">
          {/* Header: Breadcrumbs and Actions */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="/orders" className="text-xl font-bold text-black">
                    Orders
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <Skeleton className="h-6 w-24" />
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <div className="flex items-center gap-2">
              <Skeleton className="h-9 w-24" />
              <Skeleton className="h-9 w-20" />
              <Skeleton className="h-9 w-9 rounded-md" />
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:items-stretch">
            {/* Left Column (Order Info, Bundle, Note) */}
            <div className="lg:col-span-2 space-y-6 flex flex-col">
              {/* Order Info Card */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Skeleton className="h-6 w-20" />
                      <Skeleton className="h-5 w-32" />
                    </div>
                    <Skeleton className="h-10 w-32" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {[...Array(8)].map((_, i) => (
                      <div key={i}>
                        <Skeleton className="h-4 w-20 mb-2" />
                        <Skeleton className="h-5 w-24" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-grow">
                {/* Pickup Card */}
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-10 w-10 rounded-sm" />
                        <div>
                          <Skeleton className="h-5 w-32 mb-1" />
                          <Skeleton className="h-4 w-24" />
                        </div>
                      </div>
                      <Skeleton className="h-8 w-16" />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Skeleton className="h-4 w-16 mb-1" />
                      <Skeleton className="h-5 w-full" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Skeleton className="h-4 w-16 mb-1" />
                        <Skeleton className="h-5 w-24" />
                      </div>
                      <div>
                        <Skeleton className="h-4 w-16 mb-1" />
                        <Skeleton className="h-5 w-32" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Delivery Card */}
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <Skeleton className="h-5 w-32 mb-1" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                      <Skeleton className="h-8 w-16" />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Skeleton className="h-4 w-16 mb-1" />
                      <Skeleton className="h-5 w-full" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Skeleton className="h-4 w-16 mb-1" />
                        <Skeleton className="h-5 w-24" />
                      </div>
                      <div>
                        <Skeleton className="h-4 w-16 mb-1" />
                        <Skeleton className="h-5 w-32" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Right Column (Timeline, Review) */}
            <div className="lg:col-span-1 space-y-6 flex flex-col">
              {/* Timeline Card */}
              <Card className="h-full flex flex-col">
                <CardHeader>
                  <Skeleton className="h-5 w-24" />
                </CardHeader>
                <CardContent className="flex-grow overflow-y-auto max-h-[440px]">
                  <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="flex gap-3">
                        <Skeleton className="h-10 w-10 rounded-full" />
                        <div className="space-y-2 flex-1">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-20" />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Map Card */}
            <Card className="overflow-hidden h-[400px]">
              <CardContent className="p-0">
                <Skeleton className="h-[400px] w-full" />
              </CardContent>
            </Card>

            {/* Delivery Status / POD / Signature Card */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center gap-2">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-8 w-16" />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-start gap-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-6 w-8" />
                </div>
                <div>
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4 mt-2" />
                </div>
                <Skeleton className="h-px w-full" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Skeleton className="h-5 w-24 mb-2" />
                    <Skeleton className="h-32 w-full rounded" />
                  </div>
                  <div className="border-t md:border-t-0 md:border-l pt-4 md:pt-0 md:pl-4">
                    <Skeleton className="h-5 w-24 mb-2" />
                    <Skeleton className="h-32 w-full rounded" />
                  </div>
                </div>
                <Skeleton className="h-px w-full" />
                <div>
                  <Skeleton className="h-5 w-24 mb-2" />
                  <Skeleton className="h-16 w-full rounded" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailSkeleton;
